import React, { useState, useRef, useEffect } from "react";
import { X, Smartphone, Tablet, Monitor } from "lucide-react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { AspectRatioImage } from "../components/AspectRatioImage";
import DocumentPreview from "./DocumentPreview";
import { getBrandProfile } from "../../../utils/brandColors";
import { CANVAS_W, CANVAS_H } from "../../../carousel/canvas";
import { drawSlide } from "../../../carousel/drawSlide";
import { loadCarouselFonts } from "../../../carousel/fonts";
import VideoPlayer from "../../../video/VideoPlayer";
import downloadPost from "../../../assets/tips/downloadPost.svg";
import globe from "../../../assets/posts/globe.svg";
import likes from "../../../assets/posts/likes.svg";
import comment from "../../../assets/posts/comment.svg";
import repost from "../../../assets/posts/repost.svg";
import send from "../../../assets/posts/send.svg";

// design: everything drawSlide needs except the slide itself (palette, fonts,
// sizes, layout, background, logo). CarouselSection sends it up through
// onPreviewStateChange, so the preview always matches the editor.
function CarouselPreview({ slides, design = null }) {
    const canvasRef = useRef(null);
    const [index, setIndex] = useState(0);
    const [fontsReady, setFontsReady] = useState(false);

    // Redraw once the carousel fonts have loaded
    useEffect(() => {
        let alive = true;
        loadCarouselFonts().then(() => {
            if (alive) setFontsReady(true);
        });
        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !slides?.[index]) return;
        drawSlide(canvas.getContext("2d"), {
            ...(design || {}),
            slide: slides[index],
            index,
            total: slides.length,
            showLogoHandle: false,
        });
    }, [slides, index, design, fontsReady]);

    if (!slides?.length) return null;

    return (
        <div className="flex flex-col items-center gap-2">
            <canvas
                ref={canvasRef}
                width={CANVAS_W}
                height={CANVAS_H}
                className="block w-full h-auto rounded-lg"
            />
            <div className="flex items-center gap-1.5">
                {slides.map((_, i) => (
                    <button
                        key={i}
                        onClick={() => setIndex(i)}
                        className={`rounded-full transition-all ${
                            i === index
                                ? "w-3 h-3 bg-black-800"
                                : "w-2 h-2 bg-gray-300 hover:bg-gray-400"
                        }`}
                    />
                ))}
            </div>
        </div>
    );
}

export default function PreviewModal({
    open,
    onClose,
    text,
    images,
    showGeneratedImage,
    generatedImage,
    contentType,
    carouselSlides = [],
    documentFile,
    documentUrl,
    documentFilename,
    videoUrl,
    videoPosterUrl,
    onDownload,
    carouselPreview = null,
}) {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
        const el = textRef.current;
        if (!el) return;
    
        const measure = () => {
            if (!el) return;
            const hasOverflow = el.scrollHeight > el.clientHeight;
            setIsOverflowing(hasOverflow);
        };
    
        requestAnimationFrame(measure);
    
    }, [text, open]);

    const getClampClass = () => "line-clamp-3";

    const cleanHtml = (html) => {
        const tmp = document.createElement("div");
        tmp.innerHTML = html || "";
        tmp.querySelectorAll("[data-divider]").forEach((el) => el.remove());
        tmp.querySelectorAll("[data-segment]").forEach((el) => {
            el.removeAttribute("data-segment");
            el.removeAttribute("class");
            el.removeAttribute("style");
        });
        return tmp.innerHTML;
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black-900 bg-opacity-50 backdrop-blur-sm p-0">
            {/* Modal */}
            <div className="w-full max-w-[640px] mx-4 sm:mx-6 lg:mx-8 bg-white rounded-xl shadow-2xl relative max-h-[90vh] overflow-y-auto flex flex-col p-4">

                {/* Header */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="
                            flex 
                            items-center 
                            justify-between 
                            font-bold 
                            ">{t("insights.preview")}</h2>
                        <button onClick={onClose} className="hover:bg-gray-100 p-1 rounded-full transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                <div className="space-y-6 flex-grow">
                    {/* Profile Row */}
                    <div className="flex gap-3 items-start mt-2">
                        <div className="w-12 h-12 rounded-full bg-gray-200" />
                        <div>
                            {(() => {
                              const profile = getBrandProfile();
                              const name = [profile?.firstName, profile?.lastName].filter(Boolean).join(" ");
                              return <p className="font-bold text-black-800">{name || "Your Name"}</p>;
                            })()}
                            <p className="text-xs text-black-400">200,000 Followers</p>
                            <p className="text-xs text-black-400 flex">
                                1d • Edited • <img src={globe} alt="globe" className="ml-1" />
                            </p>
                        </div>
                    </div>

                    {/* Text Content */}
                    <div className="relative text-lg">
                        <div
                            ref={textRef}
                            onClick={() => !isExpanded && isOverflowing && setIsExpanded(true)} // optional click to expand
                            className={`whitespace-pre-wrap text-black-800 leading-relaxed transition-all duration-200 ${isExpanded ? "" : getClampClass()
                                }`}
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanHtml(text)) }}
                        />

                        {!isExpanded && isOverflowing && (
                            <button
                                onClick={() => setIsExpanded(true)}
                                className="text-link-500 hover:underline mt-1"
                            >
                                … more
                            </button>
                        )}
                    </div>


                    {/* Media */}
                    {contentType === "carousel" ? (
                        <CarouselPreview
                            slides={carouselSlides}
                            design={carouselPreview}
                        />
                    ) : contentType === "video" ? (
                        videoUrl && <VideoPlayer key={videoUrl} src={videoUrl} posterUrl={videoPosterUrl} />
                    ) : contentType === "document" ? (
                        <DocumentPreview
                            file={documentFile}
                            url={documentUrl}
                            filename={documentFilename}
                            height="aspect-[4/5]"
                        />
                    ) : showGeneratedImage ? (
                        <div className="flex justify-center">
                            <AspectRatioImage src={generatedImage} alt="generated preview" />
                        </div>
                    ) : (
                        images?.length > 0 && (
                            <div className="flex flex-col items-center gap-4">
                                {images.map((file, i) => (
                                    <AspectRatioImage key={i} src={URL.createObjectURL(file)} alt="preview" />
                                ))}
                            </div>
                        )
                    )}


                </div>

                <div className="flex items-center justify-between space-x-4 my-2 text-xs">
                    <span>0 {t("insights.like")}</span>
                    <div className="space-x-1">
                        <span>0 {t("insights.comment")} •</span>
                        <span>0 {t("insights.repost")}</span>
                    </div>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 mb-4 pb-2 border-b text-sm text-gray-600">
                    <button className="flex items-center space-x-1 hover:text-gray-800">
                        <img src={likes} alt="likes" /> <span>{t("insights.like")}</span>
                    </button>
                    <button className="flex items-center space-x-1 hover:text-gray-800">
                        <img src={comment} alt="comment" /> <span>{t("insights.comment")}</span>
                    </button>
                    <button className="flex items-center space-x-1 hover:text-gray-800">
                        <img src={repost} alt="repost" /> <span>{t("insights.repost")}</span>
                    </button>
                    <button className="flex items-center space-x-1 hover:text-gray-800">
                        <img src={send} alt="send" /> <span>{t("insights.send")}</span>
                    </button>
                </div>

                {/* Footer */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm sm:text-lg font-bold">
                    <button
                        onClick={onClose}
                        className="
                        flex
                        bg-black-0
                        hover:bg-black-20
                        focus:outline-none
                        focus:ring-2 
                        focus:ring-primary-400
                        text-black-800  
                        text-lg 
                        font-bold  
                        rounded-lg 
                        border-black-600
                        border-2 
                        items-center 
                        justify-center
                        w-full 
                        p-2 
                        gap-2 
                        md:w-[296px] 
                        md:text-md 
                        sm:flex-row 
                        sm:items-center 
                        disabled:opacity-50 
                        disabled:cursor-not-allowed
                        ">
                        {t("buttons.close")}
                    </button>
                    <button 
                        onClick={onDownload} 
                        className="
                        flex
                        bg-black-600
                        hover:bg-black-900
                        focus:outline-none
                        focus:ring-2 
                        focus:ring-primary-400
                        text-white 
                        text-lg 
                        font-bold  
                        rounded-lg 
                        border-black-600
                        hover:border-black-900
                        border-2 
                        items-center 
                        justify-center 
                        w-full 
                        p-2 
                        gap-2 
                        md:w-[296px] 
                        md:text-md 
                        sm:flex-row 
                        sm:items-center 
                        disabled:opacity-50 
                        disabled:cursor-not-allowed
                        ">
                        {t("buttons.downloadPost")}
                        <img src={downloadPost} alt="downloadPost" className="w-6 h-6" />
                    </button>
                </div>
            </div>
        </div>
    );
}